import React from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "@/components/lingo/DashboardLayout";
import { useAuth } from "@/lib/auth";
import { Headphones, BookOpen, PencilLine, ChevronRight, FileText, Printer, Monitor } from "lucide-react";

/**
 * IELTS Mock Hub — lets the student pick which module to mock (Listening / Reading / Writing).
 * For paper-based format, Listening also exposes the "Paper Answer Sheet" upload flow.
 */
export default function IELTSMockHub() {
  const { user } = useAuth();
  const format = user?.ielts_format || "computer";

  const modules = [
    {
      id: "listening",
      label: "Listening Mock",
      Icon: Headphones,
      color: "#0EA5E9",
      to: format === "paper" ? "/app/ielts/listening-paper" : "/app/ielts/listening-mock",
      desc: format === "paper"
        ? "Listen on a real device · transcribe answers on a printable sheet · upload photo at the end."
        : "Computer-based simulator · audio plays once · 32-minute timer · auto-submit.",
    },
    {
      id: "reading",
      label: "Reading Mock",
      Icon: BookOpen,
      color: "#16A34A",
      to: "/app/ielts/reading-mock",
      desc: "3 academic passages · 60 minutes · highlight text · TFNG · MCQ · sentence completion.",
    },
    {
      id: "writing",
      label: "Writing Mock",
      Icon: PencilLine,
      color: "#F77F00",
      to: "/app/ielts/writing-mock",
      desc: "Task 1 chart description (150+ words) and Task 2 essay (250+ words) · 60 minutes · AI-graded.",
    },
  ];

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto space-y-6" data-testid="ielts-mock-hub">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <Link to="/app/ielts/start" className="text-xs text-slate-500 hover:text-[#F77F00]">← Back</Link>
            <h1 className="font-display text-4xl text-[#003049] mt-2">IELTS Mock Tests</h1>
            <p className="text-slate-600 mt-1">Pick a module to begin a realistic, timed mock test.</p>
          </div>
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#003049] text-white text-xs">
            {format === "paper" ? <Printer className="w-4 h-4" /> : <Monitor className="w-4 h-4" />}
            {format === "paper" ? "Paper-based simulation" : "Computer-based simulation"}
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          {modules.map((m) => (
            <Link key={m.id} to={m.to} data-testid={`mock-card-${m.id}`}
              className="group bg-white border border-slate-200 rounded-3xl p-6 hover:shadow-xl hover:-translate-y-0.5 transition-all">
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4"
                style={{ background: `${m.color}1A`, color: m.color }}>
                <m.Icon className="w-7 h-7" />
              </div>
              <div className="font-display text-2xl text-[#003049]">{m.label}</div>
              <div className="text-sm text-slate-600 mt-2 leading-relaxed min-h-[60px]">{m.desc}</div>
              <div className="mt-5 inline-flex items-center gap-1 text-sm font-semibold" style={{ color: m.color }}>
                Start <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition" />
              </div>
            </Link>
          ))}
        </div>

        {format === "paper" && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 flex items-start gap-3" data-testid="paper-banner">
            <FileText className="w-5 h-5 text-amber-700 mt-0.5" />
            <div className="text-sm text-amber-900">
              <strong>Paper-based mode active.</strong> The Listening Mock will print an answer sheet for you to write on; afterwards you can upload a photo and we&apos;ll extract your answers using AI vision. You can switch to computer-based anytime from the
              {" "}<Link to="/app/ielts/start" className="underline">format picker</Link>.
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
